/**
 * vastuGrid.ts — NATIVE in-portal port of the AV Vastu Grid engine (v6.0).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (grid-first sequencing — owner lock 2026-06-07)
 * ─────────────────────────────────────────────────────────────────────────
 *  The intake is grid-first: staff trace the floor-plan boundary, the grid
 *  is generated, and THEN entrances/AUO are clicked on the map (pada/zone
 *  DERIVED from the grid). For the on-screen grid to be IDENTICAL to the
 *  report grid, the portal must run the SAME geometry the composer engine
 *  runs — ONE copy of the math, no drift.
 *
 *  The composer engine lives in the FUNCTIONS repo at
 *    firebase-functions/functions/lib/vastu-grid/{engine.js,index.js}
 *  (pure JS, `computeGrid({ boundary, rotation, options })`, called by
 *  composer/stages/ensureGrids.js). It cannot be `import`-ed across repos
 *  (separate npm packages, no shared workspace), so this file is a FAITHFUL
 *  LINE-FOR-LINE PORT of its geometry primitives, GUARDED by a golden-fixture
 *  parity test (`test/vastuGrid.test.cjs`) that asserts this port's output
 *  equals the engine's output (fixture generated FROM the engine). If the
 *  engine math ever changes, regenerate the fixture and this port must match,
 *  or the test fails — that is the no-drift contract.
 *
 *  COORDINATE SPACE (OQ-C — VERIFIED EMPIRICALLY 2026-06-07): the boundary is
 *  a traced IMAGE-PIXEL `{x,y}` polygon. The engine casts rays from the
 *  polygon centroid; zone/pada classification is scale- AND translate-
 *  invariant (verified: scale ×3 + translate → identical zone-area ratios +
 *  panchTattva). So the traced pixel polygon feeds `computeGrid` with NO
 *  transform. Areas scale with pixels² but the report only uses them as
 *  ratios (panchTattva / prakriti) — also invariant.
 *
 *  This port omits the heavy data tables (devtas/marma/chords) the on-screen
 *  preview does not need — those stay server-side. It keeps EXACTLY what the
 *  click-on-grid UI requires: centroid, the 32-ray ring L, the 16 zone
 *  polygons, the 32 entrance (pada) polygons, their areas, plus the
 *  panchTattva / prakriti share breakdown for the read-only preview.
 *
 *  Source of truth: `lib/vastu-grid/engine.js` (geometry) + `index.js`
 *  (composition). DO NOT edit the math here without a fixture regen + parity
 *  test pass. Verbatim ported 2026-06-07 — Anil.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ═══════════════════════════ types ═══════════════════════════ */

export interface Pt {
  x: number;
  y: number;
}

/** The 16 zone labels (compass), index 0..15. */
export const ZONE_LABELS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
] as const;
export type ZoneLabel = (typeof ZONE_LABELS)[number];

/**
 * 32 entrance (pada) labels — index k (1..32) = the entrance between ray
 * L[k] and L[k+1] (L33 wraps → L1). Index 0 is unused (kept so the array
 * index equals the ray index, exactly like the engine).
 * VERBATIM from engine.js ENTRANCE_LABELS.
 */
export const ENTRANCE_LABELS = [
  '', 'N5', 'N6', 'N7', 'N8', 'E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'E7', 'E8',
  'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'W1', 'W2', 'W3', 'W4', 'W5',
  'W6', 'W7', 'W8', 'N1', 'N2', 'N3', 'N4',
] as const;

/** Panch Tattva (5-element) zone groupings — VERBATIM from engine.js. */
const PANCH_TATTVA: Record<string, number[]> = {
  earth: [9, 10],
  water: [0, 1, 2, 15],
  fire: [6, 7, 8],
  air: [3, 4, 5],
  aether: [11, 12, 13, 14],
};

/**
 * Prakriti (3-element) zone groupings — VERBATIM from engine.js PRAKRITI.
 *   fire:  [4,5,6,7,8,9]   (E, ESE, SE, SSE, S, SSW)
 *   air:   [10,11,12,13,14] (SW, WSW, W, WNW, NW)
 *   water: [2,3,15,0,1]    (NE, ENE, NNW, N, NNE)
 * (These intentionally do NOT cover all 16 zones the same way panchTattva
 *  does — copied byte-for-byte so the parity test against the engine holds.)
 */
const PRAKRITI: Record<string, { zones: number[] }> = {
  fire: { zones: [4, 5, 6, 7, 8, 9] },
  air: { zones: [10, 11, 12, 13, 14] },
  water: { zones: [2, 3, 15, 0, 1] },
};

export interface GridResult {
  centroid: Pt;
  /** 32-ray ring boundary hits, keyed 1..32. */
  L: Record<number, Pt>;
  /** 16 zone polygons (each starts at centroid), index 0..15. */
  zonePolys: Pt[][];
  /** 16 zone areas (pixel²), index 0..15. */
  zoneAreas: number[];
  /** 32 pada polygons keyed by label (e.g. 'N5'). */
  entrancePolys: Record<string, Pt[]>;
  /** 32 pada areas keyed by label. */
  entranceAreas: Record<string, number>;
  /** 5-element share (sums to 1). */
  panchTattva: Record<string, number>;
  /** 3-element share (sums to 1). */
  prakriti: Record<string, number>;
  meta: { rotation: number };
}

/* ═══════════════════════ geometry primitives (verbatim) ═══════════════════════ */

export function shoelaceCentroid(pts: Pt[]): Pt {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const c = pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    a += c;
    cx += (pts[i].x + pts[j].x) * c;
    cy += (pts[i].y + pts[j].y) * c;
  }
  a /= 2;
  const f = 1 / (6 * a);
  return { x: cx * f, y: cy * f };
}

function rayDir(deg: number): { dx: number; dy: number } {
  const r = (deg * Math.PI) / 180;
  return { dx: Math.sin(r), dy: -Math.cos(r) };
}

function raySegHit(
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  p1: Pt,
  p2: Pt,
): { x: number; y: number; t: number } | null {
  const sx = p2.x - p1.x;
  const sy = p2.y - p1.y;
  const den = dx * -sy - dy * -sx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((p1.x - cx) * -sy - (p1.y - cy) * -sx) / den;
  const s = (dx * (p1.y - cy) - dy * (p1.x - cx)) / den;
  if (t >= 0 && s >= -1e-6 && s <= 1 + 1e-6) {
    return { x: cx + t * dx, y: cy + t * dy, t };
  }
  return null;
}

function compute32L(
  cx: number,
  cy: number,
  boundary: Pt[],
  rotation: number,
): Record<number, Pt> {
  const L: Record<number, Pt> = {};
  for (let i = 1; i <= 32; i++) {
    const deg = ((i - 1) * 11.25 + rotation + 360) % 360;
    const { dx, dy } = rayDir(deg);
    let best: { x: number; y: number; t: number } | null = null;
    for (let k = 0; k < boundary.length; k++) {
      const p1 = boundary[k];
      const p2 = boundary[(k + 1) % boundary.length];
      const hit = raySegHit(cx, cy, dx, dy, p1, p2);
      if (hit && (!best || hit.t < best.t)) best = hit;
    }
    L[i] = best ? { x: best.x, y: best.y } : { x: cx + dx * 100, y: cy + dy * 100 };
  }
  return L;
}

function polygonArea(poly: Pt[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const j = (i + 1) % poly.length;
    a += poly[i].x * poly[j].y - poly[j].x * poly[i].y;
  }
  return Math.abs(a / 2);
}

/* ─────────────────────── sector clipping (2026-06-07) ───────────────────────
 * (Replaced the old `outerArc` + `compassAngle`/`angleInCW`/`cwDist` arc
 *  construction — see the engine for the same change. Those helpers are gone:
 *  zone/entrance polygons now come from sector clipping, byte-parity with the
 *  engine's `clipHalf`/`sectorClipPoly`/`outerEdgeOfWedge`.)
 * CORRECTNESS FIX (port parity with engine.js): zone/entrance polygons are the
 * EXACT geometric intersection of their angular wedge (apex = centroid) with
 * the traced boundary — Sutherland–Hodgman clipping by the two half-planes
 * through the centroid along the wedge's start/end ray directions. Replaces the
 * old `outerArc` nearest-hit construction, which juts into notches on concave
 * plots and mis-sized per-zone areas by 10–20% (corrupting the panchatattva /
 * prakriti bar charts). Tiles to 0.00% on any boundary shape. Verbatim of the
 * engine `clipHalf` / `sectorClipPoly` — keeps the no-drift parity contract.
 */
function clipHalf(poly: Pt[], O: Pt, dirDeg: number, keepLeft: boolean): Pt[] {
  const r = (dirDeg * Math.PI) / 180;
  const dx = Math.sin(r);
  const dy = -Math.cos(r);
  const side = (p: Pt): boolean => {
    const cz = dx * (p.y - O.y) - dy * (p.x - O.x);
    return keepLeft ? cz <= 1e-7 : cz >= -1e-7;
  };
  const out: Pt[] = [];
  for (let i = 0; i < poly.length; i++) {
    const A = poly[i];
    const B = poly[(i + 1) % poly.length];
    const sA = side(A);
    const sB = side(B);
    if (sA) out.push(A);
    if (sA !== sB) {
      const cA = dx * (A.y - O.y) - dy * (A.x - O.x);
      const cB = dx * (B.y - O.y) - dy * (B.x - O.x);
      const t = cA / (cA - cB);
      out.push({ x: A.x + t * (B.x - A.x), y: A.y + t * (B.y - A.y) });
    }
  }
  return out;
}

/**
 * The exact polygon for the angular wedge [degS, degE] (apex = centroid) ∩ the
 * traced boundary. `degS`/`degE` are absolute compass degrees (already include
 * rotation). Replaces `outerArc` for zone + entrance area construction.
 */
function sectorClipPoly(
  degS: number,
  degE: number,
  boundary: Pt[],
  cx: number,
  cy: number,
): Pt[] {
  const O = { x: cx, y: cy };
  return clipHalf(clipHalf(boundary, O, degS, false), O, degE, true);
}

/**
 * Given a sector-clipped wedge polygon (apex = centroid), return ONLY the
 * ordered outer-wall vertices — the wedge with the centroid apex removed,
 * rotated so the sequence runs from the start-ray boundary point onward. Lets
 * us rebuild each cell as `[{cx,cy}, ...outerEdge]`, preserving the
 * centroid-first contract (door-span inward nudge / wedge-centroid label).
 * Mirrors engine.js `outerEdgeOfWedge`.
 */
function outerEdgeOfWedge(wedge: Pt[], cx: number, cy: number): Pt[] {
  if (!wedge || wedge.length < 3) return wedge || [];
  let apex = -1;
  for (let i = 0; i < wedge.length; i++) {
    if (Math.abs(wedge[i].x - cx) < 1e-6 && Math.abs(wedge[i].y - cy) < 1e-6) {
      apex = i;
      break;
    }
  }
  if (apex < 0) return wedge.slice();
  const out: Pt[] = [];
  for (let k = 1; k < wedge.length; k++) {
    out.push(wedge[(apex + k) % wedge.length]);
  }
  return out;
}

/* ═══════════════════════ composition ═══════════════════════ */

/** True if a boundary is a usable polygon (≥3 finite {x,y} points). */
export function isValidBoundary(b: unknown): b is Pt[] {
  if (!Array.isArray(b) || b.length < 3) return false;
  for (const p of b) {
    if (
      !p ||
      typeof (p as Pt).x !== 'number' ||
      typeof (p as Pt).y !== 'number' ||
      !Number.isFinite((p as Pt).x) ||
      !Number.isFinite((p as Pt).y)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Compute the Vastu grid from a traced boundary + north rotation.
 * Mirrors `lib/vastu-grid/index.js` computeGrid (geometry subset). Pure.
 *
 * @throws if the boundary is not ≥3 finite points or rotation is not finite.
 */
export function computeGrid(input: {
  boundary: Pt[];
  rotation: number;
}): GridResult {
  if (!isValidBoundary(input.boundary)) {
    throw new TypeError('boundary needs at least 3 finite {x,y} points');
  }
  if (typeof input.rotation !== 'number' || !Number.isFinite(input.rotation)) {
    throw new TypeError('rotation must be a finite number (degrees)');
  }
  const rotation = input.rotation;
  let boundary = input.boundary;

  // Auto-detect winding (v6 assumes clockwise). Reverse a CCW polygon
  // silently — owner-locked behaviour, verbatim from the engine.
  let signed = 0;
  for (let i = 0; i < boundary.length; i++) {
    const j = (i + 1) % boundary.length;
    signed += boundary[i].x * boundary[j].y - boundary[j].x * boundary[i].y;
  }
  if (signed < 0) boundary = boundary.slice().reverse();

  const centroid = shoelaceCentroid(boundary);
  const cx = centroid.x;
  const cy = centroid.y;
  const L = compute32L(cx, cy, boundary, rotation);

  // 16 zone polygons + areas (zone z spans entrance pair (2z, 2z+2); z=0 wraps).
  // CORRECTNESS FIX 2026-06-07 (parity with engine.js): each zone = its 22.5°
  // angular wedge (apex = centroid) ∩ the traced boundary, via sector clipping.
  // Replaces the old `outerArc` nearest-hit build, which mis-sized per-zone
  // areas by 10–20% on concave plots. Tiles to 0.00% on any shape.
  const zonePolys: Pt[][] = [];
  const zoneAreas: number[] = [];
  for (let z = 0; z < 16; z++) {
    let iStart = z * 2;
    if (iStart === 0) iStart = 32;
    let iEnd = z * 2 + 2;
    if (iEnd > 32) iEnd -= 32;
    const degS = ((((iStart - 1) * 11.25 + rotation) % 360) + 360) % 360;
    const degE = ((((iEnd - 1) * 11.25 + rotation) % 360) + 360) % 360;
    // Keep the centroid as the explicit first vertex so the `[{cx,cy}, ...]`
    // contract (door-span inward nudge / wedge label) still holds.
    const wedge = sectorClipPoly(degS, degE, boundary, cx, cy);
    const poly: Pt[] = [{ x: cx, y: cy }, ...outerEdgeOfWedge(wedge, cx, cy)];
    zonePolys.push(poly);
    zoneAreas.push(polygonArea(poly));
  }

  // 32 entrance (pada) polygons + areas (entrance k spans L[k]→L[k+1]).
  // Same sector-clip fix — entrances on a concave wall are correct, and the
  // door covered-pada test agrees with the drawn padas.
  const entrancePolys: Record<string, Pt[]> = {};
  const entranceAreas: Record<string, number> = {};
  for (let k = 1; k <= 32; k++) {
    const iEnd = k === 32 ? 1 : k + 1;
    const degS = ((((k - 1) * 11.25 + rotation) % 360) + 360) % 360;
    const degE = ((((iEnd - 1) * 11.25 + rotation) % 360) + 360) % 360;
    const wedge = sectorClipPoly(degS, degE, boundary, cx, cy);
    const poly: Pt[] = [{ x: cx, y: cy }, ...outerEdgeOfWedge(wedge, cx, cy)];
    const label = ENTRANCE_LABELS[k];
    entrancePolys[label] = poly;
    entranceAreas[label] = polygonArea(poly);
  }

  // Panch Tattva (5-element) + Prakriti (3-element) area shares.
  const grandTotal = zoneAreas.reduce((a, b) => a + b, 0);
  const panchTattva: Record<string, number> = {};
  for (const [name, zones] of Object.entries(PANCH_TATTVA)) {
    const sum = zones.reduce((s, z) => s + (zoneAreas[z] || 0), 0);
    panchTattva[name] = grandTotal > 0 ? sum / grandTotal : 0;
  }
  const prakriti: Record<string, number> = {};
  for (const [name, def] of Object.entries(PRAKRITI)) {
    const sum = def.zones.reduce((s, z) => s + (zoneAreas[z] || 0), 0);
    prakriti[name] = grandTotal > 0 ? sum / grandTotal : 0;
  }

  return {
    centroid: { x: cx, y: cy },
    L,
    zonePolys,
    zoneAreas,
    entrancePolys,
    entranceAreas,
    panchTattva,
    prakriti,
    meta: { rotation },
  };
}

/* ═══════════════════════ click classification (Stage 2 prep) ═══════════════════════ */

/** Even-odd point-in-polygon test. */
export function pointInPolygon(pt: Pt, poly: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    const hit =
      yi > pt.y !== yj > pt.y &&
      pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

/**
 * Which 16-zone does a clicked point fall in? Returns the compass label
 * (e.g. 'NE') or null if outside every zone. Used by the click-AUO→zone
 * step (Stage 4).
 */
export function zoneAtPoint(grid: GridResult, pt: Pt): ZoneLabel | null {
  for (let z = 0; z < grid.zonePolys.length; z++) {
    if (pointInPolygon(pt, grid.zonePolys[z])) return ZONE_LABELS[z];
  }
  return null;
}

/**
 * Which 32-pada does a clicked point fall in? Returns the pada label
 * (e.g. 'N5') or null if outside every pada. Used by the click-entrance→pada
 * step (Stage 3).
 */
export function padaAtPoint(grid: GridResult, pt: Pt): string | null {
  for (const [label, poly] of Object.entries(grid.entrancePolys)) {
    if (pointInPolygon(pt, poly)) return label;
  }
  return null;
}

/**
 * Compass angle (deg, 0..360) of a point as seen FROM the grid centroid.
 * Same convention as `gridOverlay.compassAngle` and the engine's ray cast
 * (`rayDir(deg) = (sin θ, −cos θ)`): θ=0 points up (screen-North before
 * rotation), increasing clockwise. Image-pixel space (y down). Pure.
 */
function compassAngleFromCentroid(centroid: Pt, pt: Pt): number {
  let a = (Math.atan2(pt.x - centroid.x, -(pt.y - centroid.y)) * 180) / Math.PI;
  if (a < 0) a += 360;
  return a;
}

/**
 * The entrance-pada code for an INTERIOR point — read by its pada-DIRECTION, the
 * 32-radial angular SECTOR the point's position falls in (owner ruling, memory
 * `reference_entrance_vocabulary` 2026-06-07).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY a direction read, not `padaAtPoint` (owner ruling)
 * ─────────────────────────────────────────────────────────────────────────
 *  An INTERNAL door (off a staircase / lift lobby) sits INSIDE the traced
 *  footprint, not on the perimeter. `padaAtPoint` point-in-polygons the 32 thin
 *  entrance wedges — but those wedges hug the BOUNDARY (they're the outer-wall
 *  slivers), so an interior point near the centre falls in NONE of them and
 *  returns null. Instead an internal door is analysed exactly like a perimeter
 *  entrance: its pada = the angular sector of its direction FROM the centroid.
 *
 *  Convention parity: `a = compassAngle(centroid, pt) − rotation (mod 360);
 *  padaIndex = floor(a / 11.25) % 32 + 1; label = ENTRANCE_LABELS[padaIndex]`.
 *  This is the SAME sectoring `compute32L` uses to lay the 32 rays (ray i at
 *  `(i−1)·11.25 + rotation`) and the SAME as `gridOverlay.padaOfAngle`, so the
 *  internal door's read pada matches the drawn grid's pada at that bearing —
 *  one math, on-screen == report. ONE finding per internal door (its centre
 *  direction), emitted into `entrances[]` like any other entrance.
 *
 *  Returns null only on a degenerate point exactly AT the centroid (no
 *  direction) or a non-finite point.
 */
export function padaForInteriorPoint(grid: GridResult, pt: Pt): string | null {
  if (
    !pt ||
    !Number.isFinite(pt.x) ||
    !Number.isFinite(pt.y) ||
    (pt.x === grid.centroid.x && pt.y === grid.centroid.y)
  ) {
    return null;
  }
  const ang = compassAngleFromCentroid(grid.centroid, pt);
  const rotation = grid.meta.rotation;
  const a = (((ang - rotation) % 360) + 360) % 360;
  const padaIndex = (Math.floor(a / 11.25) % 32) + 1;
  return ENTRANCE_LABELS[padaIndex] ?? null;
}

/* ═══════════════════════ region → zones[] (AUO shapes, Slice 5 redesign) ═══════════════════════ */

/**
 * An AUO SHAPE placed inside the footprint. An AUO (a bed, a cabinet, a kitchen
 * counter) is NOT a point — it has AREA and so can straddle MULTIPLE 16-zones
 * (owner ruling, 2026-06-08). Two shape kinds cover every real case:
 *   · `rect`   — square/rectangle with a `rotation` (deg, clockwise), centred at
 *                `{cx,cy}` with half-extents `hw`,`hh` (image-pixel space). The
 *                exact same rect model the door uses (centre + width + rotation).
 *   · `circle` — centred at `{cx,cy}` with radius `r`.
 * A legacy POINT AUO is just a degenerate rect (hw=hh≈0) — handled by the same
 * sampler so there is ONE region classifier, never a second.
 */
export type AuoRegion =
  | { kind: 'rect'; cx: number; cy: number; hw: number; hh: number; rotation: number }
  | { kind: 'circle'; cx: number; cy: number; r: number };

/** The four corner vertices of a rotated rect, clockwise from top-left (local). */
export function rectCorners(reg: Extract<AuoRegion, { kind: 'rect' }>): Pt[] {
  const r = (reg.rotation * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const local: Pt[] = [
    { x: -reg.hw, y: -reg.hh },
    { x: reg.hw, y: -reg.hh },
    { x: reg.hw, y: reg.hh },
    { x: -reg.hw, y: reg.hh },
  ];
  return local.map((p) => ({
    x: reg.cx + p.x * cos - p.y * sin,
    y: reg.cy + p.x * sin + p.y * cos,
  }));
}

/** True when `pt` is inside the AUO region (rect-with-rotation or circle). */
export function pointInRegion(reg: AuoRegion, pt: Pt): boolean {
  if (reg.kind === 'circle') {
    const dx = pt.x - reg.cx;
    const dy = pt.y - reg.cy;
    return dx * dx + dy * dy <= reg.r * reg.r;
  }
  // un-rotate the point into the rect's local frame, then a box test.
  const r = (-reg.rotation * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const dx = pt.x - reg.cx;
  const dy = pt.y - reg.cy;
  const lx = dx * cos - dy * sin;
  const ly = dx * sin + dy * cos;
  return Math.abs(lx) <= reg.hw && Math.abs(ly) <= reg.hh;
}

/**
 * The SET of 16-zones an AUO REGION overlaps — the region case of `zoneAtPoint`
 * (the POINT case). This is the multi-zone analogue of the door's multi-pada
 * coverage: a door covers EVERY pada its WIDTH subtends (`padasForInteriorSegment`
 * samples along the door's segment); an AUO shape covers EVERY zone its AREA
 * overlaps. We REUSE the one zone classifier (`zoneAtPoint` over the grid's own
 * `zonePolys`) — no second zone-derivation, no new geometry copy. The region is
 * sampled on a grid of points (plus its centre + corners for a tiny shape) and
 * the UNIQUE zones are collected in compass order so the review rows read
 * predictably (N, NNE, NE …). A shape entirely inside one zone returns one zone
 * (parity with the old single-zone point AUO). Returns [] when there is no grid
 * or the whole region is off the plot.
 *
 * `samples` is the per-axis density (default 9 → a 9×9 lattice clipped to the
 * region). PURE — the single shared read for the UI + persist + callable layers.
 */
export function zonesForRegion(
  grid: GridResult | null,
  region: AuoRegion | null | undefined,
  samples = 9,
): ZoneLabel[] {
  if (!grid || !region) return [];
  // bounding box of the region (pixel space).
  let minX: number;
  let minY: number;
  let maxX: number;
  let maxY: number;
  const probes: Pt[] = [];
  if (region.kind === 'circle') {
    minX = region.cx - region.r;
    maxX = region.cx + region.r;
    minY = region.cy - region.r;
    maxY = region.cy + region.r;
    probes.push({ x: region.cx, y: region.cy });
  } else {
    const corners = rectCorners(region);
    minX = Math.min(...corners.map((c) => c.x));
    maxX = Math.max(...corners.map((c) => c.x));
    minY = Math.min(...corners.map((c) => c.y));
    maxY = Math.max(...corners.map((c) => c.y));
    probes.push({ x: region.cx, y: region.cy }, ...corners);
  }
  // lattice probes that fall inside the region.
  const n = Math.max(1, samples);
  for (let i = 0; i <= n; i++) {
    for (let j = 0; j <= n; j++) {
      const x = minX + ((maxX - minX) * i) / n;
      const y = minY + ((maxY - minY) * j) / n;
      const p = { x, y };
      if (pointInRegion(region, p)) probes.push(p);
    }
  }
  const hit = new Set<number>();
  for (const p of probes) {
    for (let z = 0; z < grid.zonePolys.length; z++) {
      if (pointInPolygon(p, grid.zonePolys[z])) {
        hit.add(z);
        break;
      }
    }
  }
  // emit in compass (index) order so the rows are stable + readable.
  const out: ZoneLabel[] = [];
  for (let z = 0; z < ZONE_LABELS.length; z++) {
    if (hit.has(z)) out.push(ZONE_LABELS[z]);
  }
  return out;
}

/**
 * The set of entrance-padas an INTERNAL door SEGMENT (free endpoints `a`→`b`
 * inside the footprint) covers — by DIRECTION (owner ruling). An internal door
 * has WIDTH and ORIENTATION just like a perimeter door, so it does NOT read one
 * pada: it covers EVERY pada the angular span it subtends from the centroid
 * touches — exactly the perimeter door's "every covered pada" rule, but read by
 * DIRECTION (the 32-radial sector) instead of by boundary position.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  HOW (parity with the perimeter `cellsForSegment`)
 * ─────────────────────────────────────────────────────────────────────────
 *  Sample evenly along the segment a→b (endpoints included), take each sample's
 *  direction-pada via `padaForInteriorPoint`, and collect the UNIQUE padas in
 *  first-touched order. A short/near-centroid door subtends one sector → one
 *  pada; a wider/oriented door spanning a sector boundary → multiple, e.g.
 *  ['E4','E5']. Samples that fall exactly AT the centroid (no direction) are
 *  skipped. `samples` controls density (default 24 → never skips a thin 11.25°
 *  sector at door scale). Returns [] when there is no grid or both endpoints are
 *  degenerate.
 *
 *  PURE — the single shared read for the UI + persist layers (no second math).
 */
export function padasForInteriorSegment(
  grid: GridResult | null,
  a: Pt | null | undefined,
  b: Pt | null | undefined,
  samples = 24,
): string[] {
  if (!grid || !a || !b) return [];
  const seen = new Set<string>();
  const order: string[] = [];
  const n = Math.max(1, samples);
  // Walk t = 0 .. 1 inclusive across the segment, label each sample by direction.
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    const label = padaForInteriorPoint(grid, p);
    if (label && !seen.has(label)) {
      seen.add(label);
      order.push(label);
    }
  }
  return order;
}
